import { Box, Divider, Grid, styled, Tooltip, Typography } from '@mui/material';
import { CollapsibleSection, LocationFormInput } from 'redesign';
import { useFormContext } from 'react-hook-form';
import { FlexPoolsSection } from 'ogp/components/views/planning/work-templates/forms/shared/flex-pools/flex-pools-section';
import { DepartmentDetailResponse, WorkTemplatesResponse } from 'api';
import { useMemo } from 'react';
import { vatListOptions, WorkerEmploymentType } from '@types';
import { useFeatureFlagContext } from 'shared/contexts/feature-flag-provider';
import { IconQuestion, IconWarning } from 'assets/img';
import { PreferredWorkerOptions } from 'api/services/shift/schema/preferred-worker-options.schema';
import { useTranslation } from 'react-i18next';
import { DateTime } from 'luxon';
import { WorkFormCompanyInfo, WorkFormContext } from './work-form.types';
import { useWorkFormDefaults } from './hooks/use-work-form-defaults';
import { useWorkFormWarnings } from './hooks/use-work-form-warnings';
import { WorkFormRequiredCertificates } from './components/required-certificates';
import { useFormEmploymentCheckboxes } from './hooks/use-form-emplyoment-checkboxes';
import { useWorkFormDepartmentLocation } from './hooks/use-work-form-department-location';
import { PreferredWorkerWrapper } from './components/preferred-worker-wrapper';
import {
  useFormVisibilityRules,
  VisibilityRuleDependencies,
} from './hooks/use-form-visibility-rules';
import { hasEmploymentTypeMismatch } from './helpers/has-employment-type-mismatch';
import { RestTimeRule } from './components/rest-time-rule-wrapper';
import { JobFormElement } from '../shift-forms/components/forms/shared/inputs/job-inputs';
import { CompanyInput } from '../shift-forms/components/forms/shared/inputs/company-input';
import { ShiftDateTimeInputs } from '../shift-forms/components/forms/shared/inputs/date-time-inputs';
import { MaxClaimantsElement } from '../shift-forms/components/forms/shared/inputs/max-claimants-input';
import {
  BreakMinutesSelect,
  ClaInput,
  ContactNameInput,
  ContactPhoneWithPrefixInput,
  DepartmentSelect,
  DescriptionLegalRequirementsInput,
  FlexpoolOnlySwitch,
  HourlyRateInput,
  ImageUrlInput,
  IsEmployeeCheckbox,
  IsFreelanceCheckbox,
  IsFreelanceOrtApplicableSwitch,
  IsTempworkCheckbox,
  IsTempworkOrtApplicableSwitch,
  JobTypeSelect,
  LongDescriptionInput,
  MutableUntilHoursInput,
  ShortDescriptionInput,
  VatSelect,
  WorkTemplateCertificatesSelect,
} from '../forms/inputs';
import { FlexPoolOnlyIndicator } from '../common/flexpool-only-indicator';
import { HelperTexts, ShiftCreateDataModel } from '../shift-forms/components/forms/shared/types';
import { validateAtLeastOneEmploymentType } from '../forms/helpers/validators';
import { MultiplyDatesCalendarInput } from '../shift-forms/components/shift-multiply-calendar/multiply-dates-calendar-input';
import type { NightShiftLimitationSystem } from '../forms/inputs/rest-rule.types';

type Props = {
  showCompanySelect?: boolean;
  showTemplateSelect?: boolean;
  preferredWorkerFieldsDisabled: boolean;
  isContractFieldDisabled: boolean;
  allowDateBeforeToday: boolean;
  companies: WorkFormCompanyInfo[];
  workTemplates: WorkTemplatesResponse | undefined;
  shiftCreateData: ShiftCreateDataModel | undefined;
  defaultValues: Partial<WorkFormContext> | undefined;
  departmentData: DepartmentDetailResponse | undefined;
  preferredWorkerOptions: PreferredWorkerOptions | undefined;
  helperTexts: HelperTexts | undefined;
  showMultiplySection: boolean;
  multiplySectionAllowsPast: boolean;
  disableOrtApplicableSwitches: boolean;
  applicationsAndPlacementsEmploymentTypes: WorkerEmploymentType[];
  shouldAdhereToDrivingRestRules: boolean;
  nightShiftLimitationSystem?: NightShiftLimitationSystem;
};
export const WorkForm = ({
  showCompanySelect,
  showTemplateSelect,
  preferredWorkerFieldsDisabled,
  isContractFieldDisabled,
  preferredWorkerOptions,
  allowDateBeforeToday,
  companies,
  shiftCreateData,
  workTemplates,
  defaultValues,
  departmentData,
  showMultiplySection,
  multiplySectionAllowsPast,
  helperTexts,
  disableOrtApplicableSwitches,
  applicationsAndPlacementsEmploymentTypes = [],
}: Props) => {
  const { watch, setValue } = useFormContext<WorkFormContext>();
  const { t } = useTranslation('web-common');
  const { allowReverseChargingVAT, allowPreferredWorkers } = useFeatureFlagContext();

  const isCompanySelected = watch('companyOption') != null || !showCompanySelect;
  const selectedCompany = useMemo(
    () => companies.find((company) => company.id === watch('companyOption')?.value),
    [companies, watch('companyOption')]
  );
  // TODO: jobId => workTemplateId
  const isWorkTemplateSelected = watch('jobId') != null;
  const departmentId = watch('department');
  const isRegularWorkFieldDisabled = !isWorkTemplateSelected && !!showTemplateSelect;
  const isFreelanceDisabled = !selectedCompany?.offersFreelanceWork;
  const isFreelanceFieldDisabled = !watch('isFreelance') || isRegularWorkFieldDisabled;
  const isTempworkDisabled = !selectedCompany?.offersTempWork;
  const isTempworkFieldDisabled = !watch('isTempwork') || isRegularWorkFieldDisabled;
  const isEmployeeDisabled = !selectedCompany?.offersEmployeeWork;
  const isFreelance = watch('isFreelance');
  const isTempwork = watch('isTempwork');
  const isEmployee = watch('isEmployee');

  isContractFieldDisabled = isRegularWorkFieldDisabled || isContractFieldDisabled;
  const isFreelanceContractFieldDisabled = isFreelanceFieldDisabled || isContractFieldDisabled;
  const selectedStartDate = watch('startDate');
  const selectedEndDate = watch('endDate');
  const selectedStartTime = watch('startTime');
  const selectedEndTime = watch('endTime');
  const selectedCertificates = watch('jobCertificates') ?? [];
  const disabledDateForMultiplication = selectedStartDate
    ? DateTime.fromISO(selectedStartDate).startOf('day')
    : null;
  const flexPools = departmentData?.department?.flexPools ?? defaultValues?.flexPools ?? [];
  const visibilityRuleDependencies: VisibilityRuleDependencies = {
    isFreelance,
    isTempwork,
    selectedEndDate,
    selectedEndTime,
    selectedStartDate,
    selectedStartTime,
    selectedCertificates,
    defaultValues,
    departmentId,
  };

  // TODO: make this a part of work template form component
  const workTemplateOptions = useMemo(
    () =>
      workTemplates?.map((job) => ({
        value: job.id,
        label: (
          <FlexPoolOnlyIndicator flexPoolOnly={job.flexPoolOnly}>{job.name}</FlexPoolOnlyIndicator>
        ),
      })),
    [workTemplates]
  );

  useWorkFormDefaults(defaultValues, selectedCompany, departmentData, setValue);
  const formWarnings = useWorkFormWarnings({
    showCompanySelect,
    showTemplateSelect,
  });
  const hasEmploymentTypeMismatchWarning = useMemo(
    () =>
      hasEmploymentTypeMismatch(
        {
          isFreelance: !!isFreelance,
          isTempwork: !!isTempwork,
          isEmployee: !!isEmployee,
        },
        applicationsAndPlacementsEmploymentTypes
      ),
    [isFreelance, isTempwork, isEmployee, applicationsAndPlacementsEmploymentTypes]
  );
  const { triggerFreelanceCheckbox, triggerTempworkCheckbox, triggerEmployeeCheckbox } =
    useFormEmploymentCheckboxes();

  useWorkFormDepartmentLocation(defaultValues, departmentData);
  useFormVisibilityRules(visibilityRuleDependencies);

  return (
    <WorkFormBody>
      <CollapsibleSection
        title="Werk informatie"
        initializeAsOpen={true}
        warningText={formWarnings.workInfo}
      >
        <Grid container item columnSpacing={4} xs={12}>
          {showCompanySelect ? (
            <Grid item xs={12}>
              <CompanyInput companies={companies} disabled={false}></CompanyInput>
            </Grid>
          ) : null}
          {showTemplateSelect ? (
            <Grid item xs={12}>
              <JobFormElement
                options={workTemplateOptions ?? []}
                disabled={!isCompanySelected}
                required={isCompanySelected}
              />
            </Grid>
          ) : null}
          <Grid item xs={12}>
            <JobTypeSelect
              disabled={true}
              required={!isRegularWorkFieldDisabled}
              jobGroups={shiftCreateData?.jobGroups ?? []}
            />
          </Grid>
          <Grid item xs={12}>
            <DepartmentSelect
              options={shiftCreateData?.branchOfficesWithDepartments ?? []}
              disabled={isRegularWorkFieldDisabled}
              required={!isRegularWorkFieldDisabled}
            />
            {departmentData ? (
              <Typography variant="body2" color="text.secondary">
                De locatie van de afdeling is {departmentData.department.location.address}
              </Typography>
            ) : null}
          </Grid>
          <Grid item xs={12}>
            <StyledDivider />
          </Grid>
          <Grid item xs={12}>
            <ShiftDateTimeInputs
              disabled={isContractFieldDisabled}
              allowDateBeforeToday={allowDateBeforeToday}
              mode="create"
            />
          </Grid>
        </Grid>
      </CollapsibleSection>

      {showMultiplySection ? (
        <CollapsibleSection title={t('work_form.multiply.title')} initializeAsOpen={false}>
          <Grid container item columnSpacing={4} xs={12}>
            <Grid item xs={12}>
              <Typography>{t('work_form.multiply.calendar_explainer')}</Typography>
              <MultiplyDatesCalendarInput
                disabledDate={disabledDateForMultiplication}
                multiplySectionAllowsPast={multiplySectionAllowsPast}
              />
            </Grid>
          </Grid>
        </CollapsibleSection>
      ) : null}

      <CollapsibleSection
        title="Werk details"
        initializeAsOpen={true}
        warningText={formWarnings.workDetails}
      >
        <Grid container item columnSpacing={4} xs={12}>
          <Grid item xs={6}>
            <BreakMinutesSelect
              options={shiftCreateData?.breakOptions ?? []}
              disabled={isContractFieldDisabled}
              helperText={helperTexts?.breakMinutes}
              required={!isRegularWorkFieldDisabled}
            />
          </Grid>
          <Grid item xs={6}>
            <MaxClaimantsElement
              disabled={isRegularWorkFieldDisabled}
              required={!isRegularWorkFieldDisabled}
            />
          </Grid>
          <Grid item xs={12}>
            <StyledDivider />
          </Grid>
          {hasEmploymentTypeMismatchWarning ? (
            <Grid item xs={12}>
              <Box display="flex" alignItems="center" gap={2}>
                <StyledIcon as={IconWarning} />
                <WarinngText
                  variant="body1"
                  mt={2}
                  mb={1}
                  data-testid="employment-type-mismatch-warning"
                >
                  {t('work_form.employment_type_mismatch_with_applications')}
                </WarinngText>
              </Box>
            </Grid>
          ) : null}
          <Grid item xs={12}>
            <Typography variant="body1" mt={4} mb={2}>
              Kies één (of meerdere) dienstverband(en)
            </Typography>
          </Grid>
          {/* Freelance */}
          <Grid item xs={12}>
            <IsFreelanceCheckbox
              disabled={isFreelanceDisabled}
              onChange={triggerFreelanceCheckbox}
              validate={(val, context) => validateAtLeastOneEmploymentType(context)}
            />
          </Grid>
          <Grid item xs={12}>
            <Grid item xs={6}>
              <IsFreelanceOrtApplicableSwitch
                disabled={isFreelanceContractFieldDisabled || disableOrtApplicableSwitches}
              />
            </Grid>
          </Grid>
          <Grid item xs={6}>
            <HourlyRateInput
              disabled={isFreelanceContractFieldDisabled}
              required={!isFreelanceContractFieldDisabled}
              helperText={helperTexts?.hourlyRate}
            />
          </Grid>
          <Grid item xs={6}>
            <MutableUntilHoursInput
              disabled={isFreelanceContractFieldDisabled}
              required={!isFreelanceContractFieldDisabled}
            />
          </Grid>
          {/* Tempwork */}
          <Grid item xs={12}>
            <IsTempworkCheckbox
              disabled={isTempworkDisabled}
              onChange={triggerTempworkCheckbox}
              validate={(val, context) => validateAtLeastOneEmploymentType(context)}
            />
          </Grid>
          <Grid item xs={12}>
            <Grid item xs={6}>
              <IsTempworkOrtApplicableSwitch
                disabled={isTempworkFieldDisabled || disableOrtApplicableSwitches}
              />
            </Grid>
          </Grid>
          <Grid item xs={12}>
            <ClaInput disabled={isTempworkFieldDisabled} required={!isTempworkFieldDisabled} />
          </Grid>
          {/* Employee */}
          <Grid item xs={12}>
            <IsEmployeeCheckbox
              disabled={isEmployeeDisabled}
              validate={(val, context) => validateAtLeastOneEmploymentType(context)}
              onChange={triggerEmployeeCheckbox}
            />
          </Grid>
          <Grid item xs={12}>
            <StyledDivider />
          </Grid>
          <Grid item xs={12}>
            <LocationFormInput
              disabled={isRegularWorkFieldDisabled}
              required={!isRegularWorkFieldDisabled}
            />
          </Grid>
          <Grid item xs={6}>
            <ContactNameInput
              disabled={isRegularWorkFieldDisabled}
              required={!isRegularWorkFieldDisabled}
            />
          </Grid>
          <Grid item xs={6}>
            <ContactPhoneWithPrefixInput
              disabled={isRegularWorkFieldDisabled}
              required={!isRegularWorkFieldDisabled}
            />
          </Grid>
          <Grid item xs={6}>
            <VatSelect
              disabled={true}
              required={true}
              options={vatListOptions(allowReverseChargingVAT)}
            />
          </Grid>
        </Grid>
      </CollapsibleSection>

      <CollapsibleSection
        title="Flexpool"
        initializeAsOpen={false}
        warningText={formWarnings.flexpool}
      >
        <Grid container item columnSpacing={4} xs={12}>
          <Grid item xs={12}>
            {/* fixx this shit */}
            <FlexPoolsSection
              state={isRegularWorkFieldDisabled ? 'read' : 'create'}
              flexPoolOptions={flexPools}
            />
          </Grid>
          <Grid item xs={12} display="flex" alignItems="center" gap={4}>
            <FlexpoolOnlySwitch disabled={isRegularWorkFieldDisabled} />
            <Tooltip title="Wanneer “flexpool-only” is aangevinkt, is het werk enkel beschikbaar voor werkers in de gelinkte flexpool(s).">
              <Box height={24}>
                <IconQuestion />
              </Box>
            </Tooltip>
          </Grid>
        </Grid>
      </CollapsibleSection>

      <CollapsibleSection
        title="Beschrijving"
        initializeAsOpen={false}
        warningText={formWarnings.description}
      >
        <Grid container item columnSpacing={4} xs={12}>
          <Grid item xs={12}>
            <ImageUrlInput disabled={isRegularWorkFieldDisabled} required={false} />
          </Grid>
          <Grid item xs={12}>
            <ShortDescriptionInput
              disabled={isRegularWorkFieldDisabled}
              required={!isRegularWorkFieldDisabled}
            />
          </Grid>
          <Grid item xs={12}>
            <LongDescriptionInput
              disabled={isRegularWorkFieldDisabled}
              required={!isRegularWorkFieldDisabled}
            />
          </Grid>
          <Grid item xs={12}>
            <DescriptionLegalRequirementsInput
              disabled={isRegularWorkFieldDisabled}
              required={!isRegularWorkFieldDisabled}
            />
          </Grid>
        </Grid>
      </CollapsibleSection>

      <CollapsibleSection
        title="Certificaten"
        initializeAsOpen={false}
        warningText={formWarnings.certificates}
      >
        <Grid container item columnSpacing={4} xs={12}>
          <Grid item xs={12}>
            <WorkFormRequiredCertificates certificates={defaultValues?.requiredCertificates} />
          </Grid>
          <Grid item xs={12}>
            <WorkTemplateCertificatesSelect
              disabled={isRegularWorkFieldDisabled}
              options={shiftCreateData?.certificates ?? []}
              onChange={() => {
                setValue('visibilityRules', []);
              }}
            />
          </Grid>
        </Grid>
      </CollapsibleSection>
      {allowPreferredWorkers ? (
        <CollapsibleSection
          title="Voorkeursflexwerker"
          initializeAsOpen={false}
          warningText={formWarnings.visibilityRules}
        >
          <PreferredWorkerWrapper
            preferredWorkerOptions={preferredWorkerOptions}
            fieldsDisabled={preferredWorkerFieldsDisabled}
          />
        </CollapsibleSection>
      ) : null}
      <CollapsibleSection
        title={t('work_form.restTimeRule', { ns: 'web-common' })}
        initializeAsOpen={true}
      >
        <RestTimeRule />
      </CollapsibleSection>
    </WorkFormBody>
  );
};

const WorkFormBody = styled(Box)(({ theme }) => ({
  paddingTop: theme.spacing(4),
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(4),
}));

const StyledDivider = styled(Divider)(({ theme }) => ({
  margin: theme.spacing(2, 0),
}));

const StyledIcon = styled('svg')(({ theme }) => ({
  '& path': {
    stroke: theme.palette.lwDanger[100],
    strokeWidth: theme.spacing(0.5),
  },
}));

const WarinngText = styled(Typography)(({ theme }) => ({
  color: theme.palette.lwDanger[100],
  fontStyle: 'italic',
}));
